import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  useParams,
  Link,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Card,
  Group,
  Flex,
  Space,
  Anchor,
  Text,
  Switch,
  Box,
} from "@mantine/core";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import {
  ErrorView,
  PageTitle,
  Subnav,
  EntitySubtitles,
} from "@components/shared";
import {
  KeywordDashboard,
  KeywordContestManagement,
  KeywordSettings,
  KeywordStatus,
  KeywordWizardStatus,
} from "@components/Keyword";
import { LocationAssociate } from "@components/Location";
import { KeywordForm } from "@components/Keyword";
import { Taggit } from "@components/Tag";
import { defaultFormState } from "@components/Keyword/KeywordForm";

import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

export default function KeywordDetail() {
  const queryClient = useQueryClient();
  const [showWizard, setShowWizard] = useState(true);
  const params = useParams();
  const { id } = params;
  const [searchParams] = useSearchParams();
  const queryKey = `keywords${id}`;

  const { failureReason: error, data: info } = useQuery({
    queryKey: [queryKey],
    queryFn: async () =>
      axios
        .get(`/keywords/${params.id}/`)
        .then(({ data }) => {
          const res = data.response[0];
          // setInfo(res);
          if (
            JSON.stringify(res.wizard_settings) === "{}" ||
            searchParams.get("quickSettings") === "false"
          ) {
            setShowWizard(false);
          }

          return res;
        })
        .catch((err) => {
          throw err;
        }),
  });

  const fetchData = () => queryClient.invalidateQueries([queryKey]);

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!info) return null;

  const showLocations = false;

  const { wizard_settings: wizardSettings, wizard_status: wizardStatus } = info;
  const wizardComplete = wizardStatus.wizard_complete;
  const hasWizardSettings = JSON.stringify(wizardSettings) !== "{}";

  if (info.status === 4) {
    return (
      <div>
        <PageTitle
          icon={entityIcon.keyword()}
          title={info.text}
          subtitle={
            <React.Fragment>
              Keyword {info.author_string}{" "}
              {info.campaign_effort_id && (
                <Anchor
                  component={Link}
                  to={`/efforts/${info.campaign_effort_id}/keywords`}
                >
                  for {info.campaign_effort_name} (Effort)
                </Anchor>
              )}
            </React.Fragment>
          }
          iconColor={entityColor.keyword}
        />
        <Text>This keyword has been deleted. There's nothing to do here.</Text>
      </div>
    );
  }

  if (!wizardComplete || showWizard) {
    return (
      <div>
        <KeywordTitle info={info} />
        {wizardComplete && hasWizardSettings && (
          <WizardToggle showWizard={showWizard} setShowWizard={setShowWizard} />
        )}
        <KeywordWizardStatus
          fetchData={fetchData}
          id={id}
          effortId={info.campaign_effort_id}
          locationId={info.location_id}
          organizationId={info.organization_id}
          status={info.status}
          wizardComplete={wizardComplete}
          wizardSettings={wizardSettings}
          wizardStatus={wizardStatus}
          keywordDates={
            info.start_date && info.end_date
              ? {
                  start_date: info.start_date,
                  start_time: info.start_time,
                  start_date_formatted: info.start_date_formatted,
                  end_date: info.end_date,
                  end_date_formatted: info.end_date_formatted,
                  end_time: info.end_time,
                }
              : null
          }
        />
      </div>
    );
  }

  return (
    <div>
      <KeywordTitle info={info} />
      {wizardComplete && hasWizardSettings && (
        <WizardToggle showWizard={showWizard} setShowWizard={setShowWizard} />
      )}
      <Flex>
        <Subnav
          buttonColor={entityColor.keyword}
          links={[
            { to: `/keywords/${id}`, text: "Detail" },
            { to: `/keywords/${id}/settings`, text: "Settings" },
            { to: `/keywords/${id}/contests`, text: "Contests" },
            {
              to: `/efforts/${info.campaign_effort_id}/locations`,
              text: "Locations",
              visible: showLocations === true ? true : false,
            },
          ]
            .filter((f) =>
              // info.contest_info ||
              info.is_one_to_one ||
              (info.wizard_settings &&
                info.wizard_settings.show_contests === false)
                ? f.text !== "Contests"
                : true
            )
            .filter((f) =>
              !hasWizardSettings ? !["Settings"].includes(f.text) : true
            )}
        />
      </Flex>
      <Routes>
        <Route
          exact
          path="/"
          element={
            <React.Fragment>
              <KeywordDashboard keywordData={info} />
              <Space mt="lg" />
              <Card>
                <KeywordForm
                  id={id}
                  editability={info.editability}
                  onSuccess={fetchData}
                  locationId={info.location_id}
                  formInfo={{
                    allow_sms_claim:
                      info.allow_sms_claim || defaultFormState.allow_sms_claim,
                    end_date: info.end_date || "",
                    end_time: info.end_time || defaultFormState.end_time,
                    keyword: info.text,
                    is_staff: info.is_staff || false,
                    lock_to_location: info.lock_to_location || false,
                    start_date: info.start_date || defaultFormState.start_date,
                    start_time: info.start_time || defaultFormState.start_time,
                  }}
                />
              </Card>
              <Space mt="lg" />
              <Card>
                <KeywordStatus
                  keywordId={id}
                  status={info.status}
                  fetchData={fetchData}
                />
              </Card>
              {info.can_delete && (
                <React.Fragment>
                  <Space mt="xl" />
                  <DeleteButton id={id} fetchData={fetchData} />
                </React.Fragment>
              )}
            </React.Fragment>
          }
        />
        {(!info.is_one_to_one ||
          (info.wizard_settings &&
            info.wizard_settings.show_contests !== false)) && (
          <Route
            exact
            path="contests"
            element={
              <React.Fragment>
                {info.campaign_effort_id && (
                  <Box mb="sm">
                    <Anchor
                      fw={600}
                      c="orange"
                      component={Link}
                      to={`/efforts/${info.campaign_effort_id}/keywords?quickSettings=false`}
                    >
                      View {info.campaign_effort_name} keywords
                    </Anchor>
                  </Box>
                )}
                <KeywordContestManagement keywordId={id} />
              </React.Fragment>
            }
          />
        )}
        {hasWizardSettings && (
          <Route
            exact
            path="settings"
            element={
              <React.Fragment>
                <Card>
                  <KeywordSettings
                    keywordId={id}
                    beginDate={info.start_date}
                    endDate={info.end_date}
                  />
                </Card>
              </React.Fragment>
            }
          />
        )}
        {showLocations && (
          <Route
            exact
            path="locations"
            element={
              <React.Fragment>
                <LocationAssociate effortId={info.campaign_effort_id} />
              </React.Fragment>
            }
          />
        )}
      </Routes>
    </div>
  );
}

const DeleteButton = ({ id, fetchData }) => {
  const [loading, setLoading] = useState(false);

  function onClick() {
    setLoading(true);

    axios
      .post(`/keywords/${id}/delete/`, {})
      .then(() => {
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
        setLoading(false);
      });
  }

  return (
    <Group>
      <Button
        size="xs"
        color="gray"
        variant="subtle"
        mt="xl"
        onClick={() => {
          if (window.confirm("Are you sure you want to delete this keyword?")) {
            onClick();
          }
        }}
        disabled={loading}
        loading={loading}
        leftSection={entityIcon.trash()}
      >
        Delete this keyword
      </Button>
    </Group>
  );
};

const KeywordTitle = ({ info }) => {
  const managerInfo = useSelector((state) => state.manager);

  return (
    <React.Fragment>
      <PageTitle
        icon={entityIcon.keyword()}
        title={info.text}
        subtitle={<React.Fragment>Keyword {info.author_string}</React.Fragment>}
        iconColor={entityColor.keyword}
        style={{ marginBottom: "0" }}
      />
      <EntitySubtitles
        info={{
          ...info,
          contest_id: info.contest_info ? info.contest_info.id : null,
          contest_name: info.contest_info ? info.contest_info.name : null,
          campaign_effort_id: managerInfo ? null : info.campaign_effort_id,
        }}
        tags={<Taggit reqInfo={{ keyword_id: info.id }} />}
      />
      <Space mt="lg" />
    </React.Fragment>
  );
};

const WizardToggle = ({ showWizard, setShowWizard }) => (
  <Switch
    label="Quick Settings"
    checked={showWizard}
    onChange={() => setShowWizard(!showWizard)}
    mb="lg"
  />
);
